import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import AdminProtectedRoute from './app/AdminProtectedRoute';
import StyledToastContainer from './app/components/styledComponents/StyledToastContainer';
import ProtectedRoutes from './app/ProtectedRoutes';
import AuthRoutes from './features/auth/AuthRoutes';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import { useSelector } from 'react-redux';
import {
  RoutesData,
  TemplateRouteData,
  communicationRoutes,
} from './RoutesHelper';
import HelpSection from './features/HelpSection';
import useAuth from './hooks/useAuth';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import {
  getBugsnagKey,
  isAuthorized,
  isDisabledFunction,
} from './utils/helper';
import { Helmet } from 'react-helmet';
import StyledLoading from './app/components/loading/StyledLoading';
import RemoveBackgroundImage from './features/ImagesGallery/ProcessedImage';
import { toast } from 'react-toastify';
import { view_websites } from './utils/Constants';
import InstallPWAButton from './features/InstallPWA';
import { setAppContainerWidth } from './app/services/sideBar';
import { useDispatch } from 'react-redux';
import LogRocket from 'logrocket';

const BugSnagAPIKey = getBugsnagKey();
// Start BugSnag first...
Bugsnag.start({
  apiKey: BugSnagAPIKey,
  plugins: [new BugsnagPluginReact()],
  otherOptions: {},
  metadata: {
    app: {
      name: 'Boostfloral Backend',
    },
  },
  releaseStage: process.env.NODE_ENV,
  enabledReleaseStages: ['production'],
  onError: function (event) {
    // exclude recaptch unhandleError from bugsnag reporting
    if (
      !event ||
      !event.errors?.length ||
      !event.errors[0].errorMessage?.includes('unhandledrejection')
    ) {
      return true; // Continue reporting to Bugsnag
    }

    const excludedContexts = ['/onboarding', '/dashboard', '/login', '/signup'];
    if (
      event?.originalError?.toLowerCase().includes('timeout') &&
      excludedContexts.includes(event?.context)
    ) {
      return false;
    }
    return true;
  },
});
BugsnagPerformance.start({ apiKey: BugSnagAPIKey });

// Create the error boundary...
const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

const LazyLoadedSubscriptionModal = React.lazy(() =>
  import('./features/billing/SubscriptionModal'),
);

const publicPaths = [
  'home',
  'features',
  'pricing',
  'contact',
  'florists',
  'login',
  'signup',
  'privacy-policy',
  'terms-and-conditions',
];

function App() {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { theme, previewTheme } = useSelector((state) => state.theme);
  const floristData = useSelector((state) => state.auth.account);
  const location = useLocation();
  const navigate = useNavigate();
  const isSubscriptionModalLazyLoaded =
    LazyLoadedSubscriptionModal?._status === 1;

  const createDynamicManifest = (appName, iconUrl) => {
    const manifest = {
      short_name: 'Orders',
      name: appName,
      id: `${floristData?.id}`,
      icons: [
        {
          src: iconUrl,
          sizes: '600x600',
          type: 'image/png',
        },
      ],
      screenshots: [
        {
          src: 'https://images.boostfloral.com/image-gallery/fba3cdff-0c6f-11ef-8ae8-12b0fba41a81/Screenshot 2025-02-10 141415-100187-1739177079042.png',
          sizes: '1920x1080', // Adjusted resolution to a common wide aspect ratio
          type: 'image/png',
          label: 'Wide screenshot',
          form_factor: 'wide',
        },
        {
          src: 'https://images.boostfloral.com/image-gallery/fba3cdff-0c6f-11ef-8ae8-12b0fba41a81/Screenshot 2025-02-10 141415-100187-1739177079042.png',
          sizes: '1280x720', // Added another wide screenshot for redundancy
          type: 'image/png',
          label: 'Another wide screenshot',
          form_factor: 'wide',
        },
        {
          src: 'https://images.boostfloral.com/image-gallery/fba3cdff-0c6f-11ef-8ae8-12b0fba41a81/Screenshot 2025-02-10 141551-84588-1739177178060.png', // Update with actual path
          sizes: '450x600',
          type: 'image/png',
          label: 'Mobile screenshot', // Optional label for better context
          formFactor: 'mobile',
        },
      ],
      theme_color: '#000000',
      background_color: '#ffffff',
      display: 'standalone',
      start_url: `${window && window.location.origin}/deliveries`,
    };

    // Convert the manifest object to a JSON string
    const manifestJSON = JSON.stringify(manifest, null, 2);

    // Create a Blob from the JSON string
    const blob = new Blob([manifestJSON], { type: 'application/json' });

    // Create a link to download the blob as a file
    const url = URL.createObjectURL(blob);

    return url;
  };

  const loadManifest = () => {
    const currentPath = window.location.pathname; // Get the path from the URL

    const manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';

    // Modify the condition to match your desired path
    if (currentPath === '/deliveries') {
      manifestLink.href = createDynamicManifest(
        floristData?.floristDTO?.companyName,
        floristData?.floristDTO?.imageUrl,
      );
    } else {
      manifestLink.href = '/manifest.json';
    }

    // Remove any existing manifest link
    const existingLink = document.querySelector('link[rel="manifest"]');
    if (existingLink) {
      document.head.removeChild(existingLink);
    }

    // Append the selected manifest link to the head
    document.head.appendChild(manifestLink);
  };

  // useEffect(() => {
  //   // Only register the service worker if the current URL matches '/deliveries'
  //   if (window.location.pathname === '/deliveries') {
  //     // Register the service worker if supported
  //     if ('serviceWorker' in navigator) {
  //       window.addEventListener('load', () => {
  //         navigator.serviceWorker
  //           .register('/service-worker.js')
  //           .then((registration) => {
  //             console.log(
  //               'Service Worker registered with scope:',
  //               registration.scope,
  //             );
  //           })
  //           .catch((error) => {
  //             console.log('Service Worker registration failed:', error);
  //           });
  //       });
  //     }
  //   } else {
  //     // Unregister the service worker if the URL is not '/deliveries'
  //     if ('serviceWorker' in navigator) {
  //       navigator.serviceWorker.getRegistrations().then((registrations) => {
  //         registrations.forEach((registration) => {
  //           registration
  //             .unregister()
  //             .then(() => {
  //               console.log('Service Worker unregistered');
  //             })
  //             .catch((error) => {
  //               console.log('Service Worker unregistration failed:', error);
  //             });
  //         });
  //       });
  //     }
  //   }
  // }, []);

  const canonicalUrl =
    'https://' +
    window?.location?.host +
    (location?.pathname === '/' || location?.pathname === '/home'
      ? '/'
      : location?.pathname);

  const userPermissions = useSelector(
    (state) => state?.authorization?.userPermissions,
  );

  const has_permission_to_view_delivery_order = isAuthorized(
    userPermissions,
    'can_view_deliveries',
  );
  const isDisabled = () => {
    if (
      userPermissions &&
      userPermissions.length > 1 &&
      location?.pathname &&
      view_websites
    ) {
      const pathWithoutSlash = location?.pathname.substring(1);

      // Check for the florist role (ID 33) and allow specific paths
      if (
        floristData?.roles.some((role) => role?.id === 54 ||
        role?.name?.toLowerCase() === 'delivery driver' ||
        role?.name?.toLowerCase().includes('driver'),
      )
      ) {
        if (
          pathWithoutSlash === 'profile' ||
          pathWithoutSlash === 'settings' ||
          pathWithoutSlash === 'profile/edit'
        ) {
          return false; // Allow access to /profile and /settings
        }
      }

      const permission = view_websites.find(
        (perm) => perm?.link === pathWithoutSlash,
      );

      const isDisable = isDisabledFunction(userPermissions, permission);

      if (isDisable) {
        // Special handling for /dashboard
        if (pathWithoutSlash === 'dashboard') {
          // Find the first allowed permission that user can visit
          const firstAllowedPermission = view_websites.find((perm) =>
            userPermissions.some(
              (userPerm) => userPerm?.name === perm?.name && userPerm?.enabled,
            ),
          );

          if (firstAllowedPermission) {
            navigate(firstAllowedPermission.link); // Redirect to the allowed page
          } else {
            navigate('/settings/products'); // Or any fallback page
          }
        } else {
          const firstAllowedPermission = view_websites.find((perm) =>
            userPermissions.some(
              (userPerm) => userPerm?.name === perm?.name && userPerm?.enabled,
            ),
          );

          if (firstAllowedPermission) {
            navigate(firstAllowedPermission.link); // Redirect to the allowed page
          } else {
            navigate('/settings/products'); // Or any fallback page
          }
        }
      }
    }
  };

  useEffect(() => {
    isDisabled();
  }, [userPermissions, location?.pathname]);

  useEffect(() => {
    loadManifest();
  }, [location?.pathname]);

  // const redirectToDriverOrder = () => {
  //   if (
  //     floristData?.roles.some(
  //       (role) =>
  //         role?.id === 54 ||
  //         role?.name?.toLowerCase() === 'delivery driver' ||
  //         role?.name?.toLowerCase().includes('driver')
  //     ) &&
  //     location?.pathname !== '/deliveries' &&
  //     location?.pathname !== '/profile' &&
  //     location?.pathname !== '/settings' &&
  //     location?.pathname !== '/profile/edit'
  //   ) {
  //     navigate('/deliveries');
  //   }
  // };

  useEffect(() => {
    // Function to preload components
    isDisabled();
    if (
      location?.pathname === '/settings/inventory/Insights' ||
      location?.pathname === '/orders'
    ) {
      dispatch(setAppContainerWidth(`calc(100% - 320px)`));
    } else {
      dispatch(setAppContainerWidth(null));
    }

    // redirectToDriverOrder();
    const preloadComponents = async () => {
      const allRoutes = [
        ...TemplateRouteData,
        ...RoutesData,
        ...communicationRoutes,
      ];
      const importPaths = allRoutes
        .filter(
          (route) =>
            (route.allowedRoles?.some((role) => user?.[role]) ?? true) &&
            route?.importLocation,
        )
        .map((route) => route.importLocation);

      // Map each import path to a dynamic import statement
      const promises = importPaths.map((importPath) => import(`${importPath}`));
      // Wait for all the imports to complete
      await Promise.all(promises);
    };
    if (location?.pathname === '/dashboard') preloadComponents();
  }, [location]);

  useEffect(() => {
    if (!location) return;

    // Locate the canonical link tag
    const canonicalLink = document.querySelector("link[rel='canonical']");
    // Update the href value if the canonical link tag exists
    if (canonicalLink) {
      if (
        location?.pathname === '/' ||
        publicPaths.includes(location?.pathname?.replace('/', ''))
      ) {
        canonicalLink.setAttribute('href', canonicalUrl);
      } else {
        canonicalLink.parentNode.removeChild(canonicalLink);
      }
    } else if (
      location?.pathname === '/' ||
      publicPaths.includes(location?.pathname?.replace('/', ''))
    ) {
      // Create a new canonical link tag if it does not exist
      const newcanonicalLink = document.createElement('link');
      newcanonicalLink.setAttribute('rel', 'canonical');
      newcanonicalLink.setAttribute('href', canonicalUrl);
      document.head.appendChild(newcanonicalLink);
    }
  }, [location]);

  useEffect(() => {
    const isLogRocketEnabled =
      process.env.REACT_APP_LOGROCKET_ENABLED === 'true';
    const projectId = process.env.REACT_APP_LOGROCKET_PROJECT_ID;
    const allowedPaths = ['/onboarding', '/signup']; // Paths to enable LogRocket

    if (
      typeof window !== 'undefined' &&
      isLogRocketEnabled &&
      allowedPaths.includes(location.pathname)
    ) {
      if (projectId) {
        LogRocket.init(projectId, {
          metadata: {
            domain: window.location.hostname, // Logs the current domain
            path: location.pathname, // Adds the current path
          },
        });
      }
    }
  }, [location.pathname]); // Dependency on location.pathname

  useEffect(() => {
    const isStandalone = () => {
      return (
        window?.matchMedia('(display-mode: standalone)').matches ||
        window?.navigator?.standalone
      );
    };

    const redirectToLogin = () => {
      if (isStandalone() && !user) {
        navigate('/login');
      } else if (isStandalone() && user) {
        navigate('/deliveries');
      }
    };

    redirectToLogin();
  }, [user, navigate]);

  return (
    <>
      <ThemeProvider theme={previewTheme ?? theme ?? {}}>
        {location?.pathname === '/deliveries' && <InstallPWAButton />}
        <RemoveBackgroundImage />
        {location?.pathname !== '/' &&
          !publicPaths.includes(location?.pathname?.replace('/', '')) && (
            <Helmet>
              <title>BoostFloral</title>
            </Helmet>
          )}
        <ErrorBoundary>
          <div>
            <Routes>
              <Route path="/*" element={<AuthRoutes />} />
              <Route element={<ProtectedRoutes />}>
                <Route element={<AdminProtectedRoute />}>
                  {/* <Route path="/profile" element={<Profile />} /> */}
                  {RoutesData.filter((route) => {
                    // If only allowedRoleIds is present, check that and ignore allowedRoles
                    if (route.allowedRoleIds && !route.allowedRoles) {
                      return route.allowedRoleIds.some((roleId) => {
                        return user?.roles?.some(
                          (userRole) => userRole.id === roleId,
                        );
                      });
                    }

                    // If only allowedRoles is present, check that and ignore allowedRoleIds
                    if (route.allowedRoles && !route.allowedRoleIds) {
                      return route.allowedRoles.some((role) => user?.[role]);
                    }

                    // If both allowedRoleIds and allowedRoles are present, check both
                    if (route.allowedRoleIds && route.allowedRoles) {
                      const allowedRolesIdsMatch = route.allowedRoleIds.some(
                        (roleId) => {
                          return user?.roles?.some(
                            (userRole) => userRole.id === roleId,
                          );
                        },
                      );

                      const allowedRolesMatch = route.allowedRoles.some(
                        (role) => user?.[role],
                      );

                      return allowedRolesIdsMatch && allowedRolesMatch; // Require both to match
                    }

                    // If neither allowedRoleIds nor allowedRoles are present, return true by default
                    return true;
                  }).map((route) =>
                    route?.importLocation ? (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <Suspense fallback={<StyledLoading />}>
                            {!isSubscriptionModalLazyLoaded ? (
                              <LazyLoadedSubscriptionModal />
                            ) : (
                              <></>
                            )}
                            {React.createElement(
                              lazy(() => import(`${route?.importLocation}`)),
                              { ...route?.parameterProps },
                            )}
                          </Suspense>
                        }
                      />
                    ) : (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <Suspense fallback={<StyledLoading />}>
                            {!isSubscriptionModalLazyLoaded ? (
                              <LazyLoadedSubscriptionModal />
                            ) : (
                              <></>
                            )}
                            {route?.component}
                          </Suspense>
                        }
                      />
                    ),
                  )}
                </Route>
                {TemplateRouteData.map((route) =>
                  route?.importLocation ? (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        <Suspense fallback={<StyledLoading />}>
                          {React.createElement(
                            lazy(() => import(`${route?.importLocation}`)),
                          )}
                        </Suspense>
                      }
                    />
                  ) : (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={route?.component}
                    />
                  ),
                )}
              </Route>
            </Routes>

            <HelpSection />
            <StyledToastContainer
              autoClose={1500}
              hideProgressBar
              pauseOnFocusLoss
              draggable
              theme="colored"
            />
            <GlobalStyle />
          </div>
        </ErrorBoundary>
      </ThemeProvider>
    </>
  );
}

const GlobalStyle = createGlobalStyle`
  .dragging-item{
  z-index:99999 !important;
  }
  .user-mention, a.mention {
    color: ${({ theme }) => theme.badgeText} !important;
    background-color: ${({ theme }) => theme.badgeText}1a;
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 12px;
    display: inline-block;
    position: relative;
    z-index: 2;
    line-height: normal;
  }

  .tribute-container {
    z-index: 9999999;
  }
  .tribute-container {
    ul {
      border-radius: 6px;
      overflow: hidden;
      padding-left: 0;
      margin-left: 20px;
      background-color: #fff;
      border: 1px solid #ededed;
      box-shadow: 2px 2px 31px -10px rgb(0 0 0 / 25%);
    }
  }
  .user-mention-item {
    cursor: pointer;
    list-style: none;
    padding: 6px 10px;
    font-weight: 400;
    transition: all 0.1s ease-in-out;
    img {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      margin-right: 8px;
    }
    span.mentionUsername {
      margin-right: 4px;
    }
    
    span {
      font-size: 13px;
      color: ${({ theme }) => theme.placeholderColor}
    }
  }
  .highlight {
    transition: all 0.1s ease-in-out;
    background-color: ${({ theme }) => theme.inputBorder};
    span.mentionUsername {
      font-weight: 600 !important;
    }
  }
`;

export default App;
